import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { KoobOrganizationPicker, KoobSidebar, icons } from '@koob/ui';
import { switchActingOrganization } from '@koob/utils';
import { openTripWizard } from '@koob/ui/src/features/tripWizard';
import {
  useApp,
  useAuth,
  useNotifications,
  useResetTripDesigner,
  useSearchParams,
} from 'hooks';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import {
  getAllDmcRequestAccess,
  getDmcRequestAccess,
} from 'api/node/dmcRequestAccess';
import { permissionsEnum, stateEnum } from 'utils';
import NotificationType from 'constants/NotificationType';
import { getSettingRequests } from 'api/node/settingRequests';
import { getToRequestAccess } from 'api/node/toRequestAccess';

const useFilteredQuery = (kind) => {
  return useAsync({
    promiseFn: getSettingRequests,
    filters: {
      discarded: false,
      state: stateEnum.PENDING,
      kind: kind,
    },
  });
};

const canDisplayNotifications = (pathname, segment) => {
  return pathname.split('/').indexOf(segment) === -1;
};

const MainSidebar = () => {
  const {
    isAllowedTo,
    currentOrganizationId,
    currentOrganization,
    user,
    canManageToRequestAccess,
  } = useApp();
  const resetContext = useResetTripDesigner();
  const { t } = useTranslation('mainSidebar');
  const { logout, impersonating, stopImpersonating } = useAuth();
  const [{ state }] = useSearchParams();
  const { pathname } = useLocation();
  const { notifications } = useNotifications();
  const [isValidTripDesignerRequest, setIsValidTripDesignerRequest] = useState(
    localStorage.getItem('tripDesignerRequestState') === stateEnum.VALIDATED,
  );

  const canDisplayTripNotification = canDisplayNotifications(
    pathname,
    'trip-designer',
  );

  const canDisplayBookingsNotifications = canDisplayNotifications(
    pathname,
    'bookings',
  );

  const { data, reload: refetch } = useFilteredQuery(undefined);
  const { data: hotelSettingsPending, reload: refetchHotelSettingCount } =
    useFilteredQuery('hotel');

  const requestFilters = {
    tripDesignerRequestState: stateEnum.PENDING,
    state: stateEnum.VALIDATED,
  };

  const { data: requestAccess, reload: requestAccessReload } = useAsync({
    promiseFn: getToRequestAccess,
    filters: requestFilters,
    skip: !isAllowedTo('canManageToRequestAccess'),
    watchFn: (props, prevProps) => {
      return !isEqual(props.filters, prevProps.filters);
    },
  });

  const { data: dmcRequestAccess, reload: allDmcRequestAccessReload } =
    useAsync({
      promiseFn: getAllDmcRequestAccess,
      skip: !isAllowedTo('canManageToRequestAccess'),
      filters: {
        tripDesignerRequestState: stateEnum.PENDING,
      },
    });

  const { data: dmcRequest, reload: reloadDmcRequestAccess } = useAsync({
    promiseFn: getDmcRequestAccess,
  });

  useEffect(() => {
    if (impersonating) {
      reloadDmcRequestAccess();
    }
  }, [impersonating, reloadDmcRequestAccess]);

  const {
    data: experienceSettingsPending,
    reload: refetchExperienceSettingCount,
  } = useFilteredQuery('experience');

  const tripsNotificationsCounter = notifications.filter(
    (notification) =>
      [
        NotificationType.TRIP_HELP_REQUESTED,
        NotificationType.TRIP_HELP_ASSISTANT,
        NotificationType.TRIP_NEW_VERSION_AVAILABLE,
        NotificationType.TRIP_BOOKING_CONFIRMED,
      ].includes(notification.notificationType) && !notification.read,
  ).length;

  const bookingNotificationsCounter = notifications.filter(
    ({ notificationType, read }) =>
      /^(booking_.+|update_hotel|update_experience)$/.test(notificationType) &&
      !read,
  ).length;

  const connectionRequestNotificationsCounter = notifications.filter(
    ({ notificationType, read }) =>
      /^to_connection_request_/.test(notificationType) && !read,
  ).length;

  useEffect(() => {
    refetch();
    requestAccessReload();
    allDmcRequestAccessReload();
    refetchHotelSettingCount();
    refetchExperienceSettingCount();
  }, [
    refetch,
    refetchExperienceSettingCount,
    refetchHotelSettingCount,
    requestAccessReload,
    allDmcRequestAccessReload,
    state,
  ]);

  useEffect(() => {
    const isValid =
      dmcRequest?.data?.tripDesignerRequestState === stateEnum.VALIDATED;
    setIsValidTripDesignerRequest(isValid);
    localStorage.setItem(
      'tripDesignerRequestState',
      isValid ? stateEnum.VALIDATED : '',
    );
  }, [dmcRequest?.data?.tripDesignerRequestState]);

  const toRequestAccesCount = requestAccess?.data?.length ?? 0;
  const dmcRequestAccesCount = dmcRequestAccess?.data?.length ?? 0;
  const requestAccesCount = toRequestAccesCount + dmcRequestAccesCount;

  const settingCounts = {};
  const tab = data?.data ?? [];
  for (const item of tab) {
    const settingName = item.settingName;
    settingCounts[settingName] = (settingCounts[settingName] || 0) + 1;
  }

  const totalExperienceSettingPending =
    experienceSettingsPending?.settingRequests?.totalCount || 0;

  const totalPending = hotelSettingsPending?.settingRequests?.totalCount || 0;

  const facility_group =
    (settingCounts['facility'] ?? 0) + (settingCounts['group'] ?? 0);

  // Build main navigation links
  const mainLinks = [];

  if (isAllowedTo('manageOrganizations')) {
    mainLinks.push({
      to: '/organizations',
      icon: icons.organizations,
      label: t('organizations'),
      badge:
        isAllowedTo('canManageToRequestAccess') && requestAccesCount > 0
          ? requestAccesCount
          : null,
    });
  }

  if (isAllowedTo('readSelfOrganization')) {
    mainLinks.push({
      to: `/organizations/${currentOrganizationId}`,
      icon: icons.myOrganization,
      label: t('myOrganization'),
    });
  }

  if (isAllowedTo('readBookings')) {
    mainLinks.push({
      to: '/bookings/my-bookings',
      icon: icons.bookings,
      label: t('bookings'),
      badge:
        canDisplayBookingsNotifications && bookingNotificationsCounter > 0
          ? bookingNotificationsCounter
          : null,
      children: [
        {
          to: '/bookings/my-bookings',
          label: t('booking'),
        },
        {
          to: '/bookings/partner-bookings',
          label: t('bookingPartners'),
          badge:
            bookingNotificationsCounter > 0
              ? bookingNotificationsCounter
              : null,
        },
        ...(isAllowedTo('manageBookings')
          ? [
              {
                to: '/bookings/new/hotel',
                icon: icons.search,
                label: t('hotelSearch'),
              },
            ]
          : []),
      ],
    });
  }

  mainLinks.push({
    to: '/hotels/catalog',
    icon: icons.hotels,
    label: t('hotels'),
  });

  mainLinks.push({
    to: '/experiences',
    icon: icons.experiences,
    label: t('experiences'),
  });

  if (isAllowedTo(permissionsEnum.READ_BOOKINGS)) {
    const tripDesignerLink = {
      to: !isValidTripDesignerRequest
        ? '/trip-designer/'
        : '/trip-designer/trips',
      icon: icons.tripDesigner,
      label: t('tripDesigner'),
      dataCy: 'trip-designer',
      badge:
        canDisplayTripNotification && tripsNotificationsCounter > 0
          ? tripsNotificationsCounter
          : null,
    };

    if (isValidTripDesignerRequest) {
      // Trip Designer is pinned open: children always visible, no chevron.
      tripDesignerLink.alwaysExpanded = true;
      tripDesignerLink.children = [
        {
          label: t('designTrip'),
          dataCy: 'design-trip',
          // Opens over the current page instead of navigating away.
          onClick: () => {
            resetContext();
            openTripWizard();
          },
        },
        {
          to: '/trip-designer/trips',
          label: t('trips'),
        },
        {
          to: '/trip-designer/library/trips',
          label: t('library'),
          badge:
            tripsNotificationsCounter > 0 ? tripsNotificationsCounter : null,
        },
      ];
    }

    mainLinks.push(tripDesignerLink);
  }

  if (isAllowedTo('readUsers')) {
    mainLinks.push({
      to: '/users',
      icon: icons.users,
      label: t('users'),
    });
  }

  if (isAllowedTo('readLocations')) {
    mainLinks.push({
      to: '/locations',
      icon: icons.locations,
      label: t('locations'),
      children: [
        { to: '/locations/countries', label: t('countries') },
        { to: '/locations/cities', label: t('cities') },
        { to: '/locations/markets', label: t('markets') },
      ],
    });
  }

  // Build settings links
  const settingsLinks = [];

  if (isAllowedTo('readOrganizationSettings')) {
    settingsLinks.push({
      to: '/settings/organizations/api-keys',
      icon: icons.agency,
      label: t('organizationsSettings'),
      children: [
        { to: '/settings/organizations/api-keys', label: t('apiKeys') },
        {
          to: '/settings/organizations/partnerships',
          label: t('partnerships'),
        },
      ],
    });
  }

  if (isAllowedTo('manageErrorDictionary')) {
    settingsLinks.push({
      to: '/settings/error-dictionary',
      icon: icons.docs,
      label: t('errorDictionary'),
    });
  }

  if (isAllowedTo('manageIntegrationsSettings')) {
    settingsLinks.push({
      to: '/settings/integrations/staah',
      icon: icons.integrations,
      label: t('integrationsSettings'),
      children: [
        {
          to: '/settings/integrations/connections',
          label: t('integrationsConnections'),
        },
        { to: '/settings/integrations/staah', label: t('staah') },
        { to: '/settings/integrations/tl-lincoln', label: t('tlLincoln') },
        { to: '/settings/integrations/mcp', label: t('mcp') },
        { to: '/settings/integrations/jobs', label: t('integrationsJobs') },
      ],
    });
  }

  if (isAllowedTo('readAuditLog')) {
    settingsLinks.push({
      to: '/settings/audit-log',
      icon: icons.history,
      label: t('auditLog'),
    });
  }

  if (isAllowedTo('readHotelSettings')) {
    const hotelSettingsChildren = [
      {
        to: '/settings/hotels/categories',
        label: t('categories'),
        badge: settingCounts['category'] || null,
      },
      {
        to: '/settings/hotels/styles',
        label: t('styles'),
        badge: settingCounts['style'] || null,
      },
      {
        to: '/settings/hotels/locations',
        label: t('locations'),
        badge: settingCounts['location'] || null,
      },
      {
        to: '/settings/hotels/accreditations',
        label: t('accreditations'),
        badge: settingCounts['accreditations'] || null,
      },
      {
        to: '/settings/hotels/general-categories',
        label: t('descriptions'),
        badge: settingCounts['descriptions'] || null,
      },
      {
        to: '/settings/hotels/dmc-categories',
        label: t('dmcCategories'),
        badge: settingCounts['dmc-category'] || null,
      },
      {
        to: '/settings/hotels/room-types',
        label: t('roomTypes'),
        badge: settingCounts['room'] || null,
      },
      {
        to: '/settings/hotels/bed-types',
        label: t('bedTypes'),
        badge: settingCounts['bed'] || null,
      },
      {
        to: '/settings/hotels/sustainable-clusters',
        label: t('sustainableClusters'),
        badge: settingCounts['sustainable'] || null,
      },
      {
        to: '/settings/hotels/facilities-groups',
        label: t('facilitiesGroups'),
        badge: facility_group || null,
      },
      {
        to: '/settings/hotels/meal-plans',
        label: t('mealPlans'),
        badge: settingCounts['meal-plan'] || null,
      },
    ];

    if (!isAllowedTo('manageHotelSettings')) {
      hotelSettingsChildren.forEach((c) => {
        c.badge = null;
      });
    }

    settingsLinks.push({
      to: '/settings/hotels/categories',
      icon: icons.hotels,
      label: t('hotelsSettings'),
      badge:
        isAllowedTo('manageHotelSettings') && totalPending > 0
          ? totalPending
          : null,
      children: hotelSettingsChildren,
    });
  }

  if (isAllowedTo('readExperienceSettings')) {
    const experienceSettingsChildren = [
      {
        to: '/settings/experiences/segments',
        label: t('segments'),
        badge: settingCounts['segment'] || null,
      },
      {
        to: '/settings/experiences/themes',
        label: t('themes'),
        badge: settingCounts['theme'] || null,
      },
    ];

    if (!isAllowedTo('manageHotelSettings')) {
      experienceSettingsChildren.forEach((c) => {
        c.badge = null;
      });
    }

    settingsLinks.push({
      to: '/settings/experiences',
      icon: icons.experiences,
      label: t('experiencesSettings'),
      badge:
        isAllowedTo('manageHotelSettings') && totalExperienceSettingPending > 0
          ? totalExperienceSettingPending
          : null,
      children: experienceSettingsChildren,
    });
  }

  if (isAllowedTo('readTravelerSettings')) {
    settingsLinks.push({
      to: '/settings/travelers',
      icon: icons.travelersSettings,
      label: t('travelersSettings'),
      children: [
        { to: '/settings/travelers/traveler-kinds', label: t('categories') },
      ],
    });
  }

  if (
    isAllowedTo('readTravelDiarySettings') ||
    isAllowedTo('readTravelDiaryContent')
  ) {
    const travelDiaryChildren = [];

    if (isAllowedTo('readTravelDiarySettings')) {
      travelDiaryChildren.push({
        to: '/settings/travel-diary/sections',
        label: t('travelDiarySection'),
      });
    }

    if (isAllowedTo('readTravelDiaryContent')) {
      travelDiaryChildren.push({
        to: '/settings/travel-diary/content-library',
        label: t('travelDiaryContentLibrary'),
      });
    }

    settingsLinks.push({
      to: '/settings/travel-diary',
      icon: icons.travelDiary,
      label: t('travelDiarySettings'),
      children: travelDiaryChildren,
    });
  }

  if (
    isAllowedTo('canManageToRequestAccess') ||
    canManageToRequestAccess(user)
  ) {
    settingsLinks.push({
      to: '/to-request-access',
      icon: icons.requestAccess,
      label: t('requestAccess'),
    });
  }

  const toConnectionRequestChildren = [
    {
      to: '/to-connection-request/connections',
      label: t('connections'),
    },
    {
      to: '/to-connection-request/bulk-edit',
      label: t('bulkEdit'),
    },
    {
      to: '/to-connection-request/models',
      label: t('connectionRequestModels'),
    },
    {
      to: '/to-connection-request/promotions',
      label: t('promotions'),
    },
  ];

  settingsLinks.push({
    to: '/to-connection-request',
    icon: icons.distribution,
    label: t('connectionRequest'),
    badge:
      connectionRequestNotificationsCounter > 0
        ? connectionRequestNotificationsCounter
        : null,
    children: toConnectionRequestChildren,
  });

  settingsLinks.push({
    to: '/allotments',
    icon: icons.allotments,
    label: t('allotments'),
  });

  settingsLinks.push({
    to: '/night-rates',
    icon: icons.nightRates,
    label: t('nightRates'),
  });

  const userName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`
    : user?.email;

  const organizationName =
    currentOrganization?.displayName || user?.organizationName;

  return (
    <KoobSidebar
      sections={[
        { title: null, links: mainLinks },
        { title: t('settings'), links: settingsLinks },
      ]}
      footerLinks={[
        {
          label: t('userGuide'),
          icon: icons.supportCenter,
          href: 'https://support.koob.tech/en/',
          external: true,
        },
      ]}
      user={{
        name: userName,
        organization: organizationName,
      }}
      userSlot={(collapsed) =>
        !collapsed && (
          <KoobOrganizationPicker
            className="px-4 pb-3"
            organizations={user?.organizations ?? []}
            currentOrganizationId={currentOrganization?.organizationId}
            onChange={switchActingOrganization}
            label={t('organizationPicker')}
            groupLabel={(name) => t('allOrganizationsOf', { name })}
          />
        )
      }
      impersonating={impersonating}
      onLogout={logout}
      onStopImpersonating={stopImpersonating}
      router={{ NavLink, useLocation }}
    />
  );
};

export default MainSidebar;
